// src/firebase/solutions.js
// Head-marker-authored model/marked solution per homework type, shown to
// students alongside their own marked-up submission once it's released to
// them. Mirrors examMaterials.js/comments.js: the Storage path (not a
// tokenized URL) is stored in Firestore, and an authenticated URL is
// generated on demand via submissions.js's getAuthenticatedFileUrl.

import { doc, getDoc, setDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytesResumable, deleteObject } from 'firebase/storage';
import { db, storage } from './config';
import { getCurrentUserOrgId } from './orgs';
import { getHomeworkNames, sortHomeworkTypes } from './settings';

// Each homework type's solution lives at orgs/{orgId}/solutions/{homeworkType}.
// Every function below accepts an optional trailing orgId that overrides the
// auto-resolved "current signed-in user's org" - see settings.js for the
// same pattern and when to pass it explicitly.
const getSolutionRef = async (homeworkType, orgId) => {
  const resolvedOrgId = orgId || await getCurrentUserOrgId();
  return doc(db, 'orgs', resolvedOrgId, 'solutions', homeworkType);
};

/**
 * Get the model solution for a single homework type, or null if none has
 * been uploaded yet.
 */
export const getSolution = async (homeworkType, orgId) => {
  try {
    const docRef = await getSolutionRef(homeworkType, orgId);
    const docSnap = await getDoc(docRef);
    return docSnap.exists() ? docSnap.data() : null;
  } catch (error) {
    console.error('Get solution error:', error);
    return null;
  }
};

/**
 * Get model solutions for every homework type in the org, as
 * { [homeworkType]: { filePath, fileName, updatedAt } | null }.
 */
export const getAllSolutions = async (orgId) => {
  try {
    const resolvedOrgId = orgId || await getCurrentUserOrgId();
    const names = await getHomeworkNames(resolvedOrgId);
    const homeworkTypes = sortHomeworkTypes(Object.keys(names));
    const results = await Promise.all(homeworkTypes.map(type => getSolution(type, resolvedOrgId)));
    return Object.fromEntries(homeworkTypes.map((type, i) => [type, results[i]]));
  } catch (error) {
    console.error('Get all solutions error:', error);
    return {};
  }
};

/**
 * Upload (or replace) the model solution file for a homework type. Any
 * previous file for the same type is deleted from Storage first so a
 * replace doesn't leak the old file.
 */
export const uploadSolution = async (homeworkType, file, orgId) => {
  const resolvedOrgId = orgId || await getCurrentUserOrgId();
  const existing = await getSolution(homeworkType, resolvedOrgId);
  if (existing?.filePath) {
    await deleteObject(ref(storage, existing.filePath)).catch(() => {});
  }

  const filePath = `solutions/${resolvedOrgId}/${homeworkType}/${Date.now()}_${file.name}`;
  await uploadBytesResumable(ref(storage, filePath), file);

  const docRef = await getSolutionRef(homeworkType, resolvedOrgId);
  const data = { filePath, fileName: file.name, updatedAt: serverTimestamp() };
  await setDoc(docRef, data);
  return data;
};

/**
 * Remove the model solution for a homework type entirely.
 */
export const deleteSolution = async (homeworkType, orgId) => {
  const resolvedOrgId = orgId || await getCurrentUserOrgId();
  const existing = await getSolution(homeworkType, resolvedOrgId);
  if (existing?.filePath) {
    await deleteObject(ref(storage, existing.filePath)).catch(() => {});
  }
  const docRef = await getSolutionRef(homeworkType, resolvedOrgId);
  await deleteDoc(docRef);
};
